import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ForceLightMode from "@/components/ForceLightMode";
import { DynamicScripts } from "@/components/DynamicScripts";
import { InstallAppBanner } from "@/components/InstallAppBanner";


// Eagerly load the landing page for fast initial paint
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Quotes = lazy(() => import("./pages/Quotes"));
const QuoteNew = lazy(() => import("./pages/QuoteNew"));
const QuoteDetail = lazy(() => import("./pages/QuoteDetail"));
const QuotePublic = lazy(() => import("./pages/QuotePublic"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const InvoicePublic = lazy(() => import("./pages/InvoicePublic"));
const InvoiceNew = lazy(() => import("./pages/InvoiceNew"));
const InvoiceEdit = lazy(() => import("./pages/InvoiceEdit"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientNew = lazy(() => import("./pages/ClientNew"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const Services = lazy(() => import("./pages/Services"));
const QuoteEdit = lazy(() => import("./pages/QuoteEdit"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));

const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const BookingSettings = lazy(() => import("./pages/BookingSettings"));
const BookingPublic = lazy(() => import("./pages/BookingPublic"));
const BookingEmbed = lazy(() => import("./pages/BookingEmbed"));
const Appointments = lazy(() => import("./pages/Appointments"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const AppointmentPublic = lazy(() => import("./pages/AppointmentPublic"));
const BrandingSettings = lazy(() => import("./pages/BrandingSettings"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SMSConsent = lazy(() => import("./pages/SMSConsent"));
const SupportDocs = lazy(() => import("./pages/SupportDocs"));
const ReminderSettings = lazy(() => import("./pages/ReminderSettings"));
const SMSNotificationSettings = lazy(() => import("./pages/SMSNotificationSettings"));
const Support = lazy(() => import("./pages/Support"));
const ClientLogin = lazy(() => import("./pages/ClientLogin"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const BetaLanding = lazy(() => import("./pages/BetaLanding"));
const Messages = lazy(() => import("./pages/Messages"));
const Images = lazy(() => import("./pages/Images"));
const JVZoo = lazy(() => import("./pages/JVZoo"));
const JVPartners = lazy(() => import("./pages/JVPartners"));
const JVZooOTO1 = lazy(() => import("./pages/JVZooBundle"));
const JVZooOTO2 = lazy(() => import("./pages/JVZooOTO"));
const JVZooOTO3 = lazy(() => import("./pages/JVZooOTO2"));
const JVZooOTO3Downsell = lazy(() => import("./pages/JVZooOTO3Downsell"));

const Install = lazy(() => import("./pages/Install"));
const Templates = lazy(() => import("./pages/Templates"));
const Reports = lazy(() => import("./pages/Reports"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PreLaunch = lazy(() => import("./pages/PreLaunch"));
const PreLaunchBundle = lazy(() => import("./pages/PreLaunchBundle"));
const PreLaunchContestHero = lazy(() => import("./pages/PreLaunchContestHero"));
const FunnelContestHero = lazy(() => import("./pages/FunnelContestHero"));
const MunchEyeBanner = lazy(() => import("./pages/MunchEyeBanner"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const PublicRoadmap = lazy(() => import("./pages/PublicRoadmap"));
const TeamMembers = lazy(() => import("./pages/TeamMembers"));
const WebsiteSettings = lazy(() => import("./pages/WebsiteSettings"));
const SitePublic = lazy(() => import("./pages/SitePublic"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));
const Presentation = lazy(() => import("./pages/Presentation"));
const ReviewSettings = lazy(() => import("./pages/ReviewSettings"));
const CalendarView = lazy(() => import("./pages/CalendarView"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const Founders = lazy(() => import("./pages/Founders"));
const TimeClock = lazy(() => import("./pages/TimeClock"));
const TimeClockAdmin = lazy(() => import("./pages/TimeClockAdmin"));
const ClockKiosk = lazy(() => import("./pages/ClockKiosk"));

// Admin panel (separate layout)
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrganizations = lazy(() => import("./pages/AdminOrganizations"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminInbox = lazy(() => import("./pages/AdminInbox"));
const AdminBroadcasts = lazy(() => import("./pages/AdminBroadcasts"));
const AdminVideoAnalytics = lazy(() => import("./pages/AdminVideoAnalytics"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminIPNSimulator = lazy(() => import("./pages/AdminIPNSimulator"));
const AdminScripts = lazy(() => import("./pages/AdminScripts"));
const AdminAffiliates = lazy(() => import("./pages/AdminAffiliates"));
const AffiliateLanding = lazy(() => import("./pages/AffiliateLanding"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));

// Future features - backend ready, frontend hidden
// const Expenses = lazy(() => import("./pages/Expenses"));
// const CustomDomainSettings = lazy(() => import("./pages/CustomDomainSettings"));
// const CalendarSyncSettings = lazy(() => import("./pages/CalendarSyncSettings"));
// const WebhookSettings = lazy(() => import("./pages/WebhookSettings"));
// const PushNotificationSettings = lazy(() => import("./pages/PushNotificationSettings"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <DynamicScripts />
      <BrowserRouter>
        <InstallAppBanner />
        <Suspense fallback={<PageLoader />}>

          <Routes>
            <Route path="/auth" element={<ForceLightMode><Auth /></ForceLightMode>} />
            <Route path="/q/:token" element={<ForceLightMode><QuotePublic /></ForceLightMode>} />
            <Route path="/quote/:token" element={<ForceLightMode><QuotePublic /></ForceLightMode>} />
            <Route path="/i/:token" element={<ForceLightMode><InvoicePublic /></ForceLightMode>} />
            <Route path="/invoice/:token" element={<ForceLightMode><InvoicePublic /></ForceLightMode>} />
            <Route path="/a/:token" element={<ForceLightMode><AppointmentPublic /></ForceLightMode>} />
            <Route path="/appointment/:token" element={<ForceLightMode><AppointmentPublic /></ForceLightMode>} />
            
            <Route path="/" element={<ForceLightMode><Founders /></ForceLightMode>} />
            <Route path="/preview-index" element={<ForceLightMode><Index /></ForceLightMode>} />
            <Route path="/beta" element={<ForceLightMode><BetaLanding /></ForceLightMode>} />
            <Route path="/jvzoo" element={<ForceLightMode><JVZoo /></ForceLightMode>} />
            <Route path="/jv" element={<ForceLightMode><JVPartners /></ForceLightMode>} />
            <Route path="/jvzoo/oto1" element={<ForceLightMode><JVZooOTO1 /></ForceLightMode>} />
            <Route path="/jvzoo/oto2" element={<ForceLightMode><JVZooOTO2 /></ForceLightMode>} />
            <Route path="/jvzoo/oto3" element={<ForceLightMode><JVZooOTO3 /></ForceLightMode>} />
            <Route path="/jvzoo/oto3-downsell" element={<ForceLightMode><JVZooOTO3Downsell /></ForceLightMode>} />
            {/* Legacy redirects */}
            <Route path="/jvzoo/bundle" element={<Navigate to="/jvzoo/oto1" replace />} />
            <Route path="/jvzoo/oto" element={<Navigate to="/jvzoo/oto2" replace />} />

            <Route path="/install" element={<ForceLightMode><Install /></ForceLightMode>} />
            <Route path="/thank-you" element={<ForceLightMode><ThankYou /></ForceLightMode>} />
            <Route path="/launch" element={<ForceLightMode><PreLaunch /></ForceLightMode>} />
            <Route path="/launch/bundle" element={<ForceLightMode><PreLaunchBundle /></ForceLightMode>} />
            <Route path="/launch/contest" element={<ForceLightMode><PreLaunchContestHero /></ForceLightMode>} />
            <Route path="/launch/funnel-contest" element={<ForceLightMode><FunnelContestHero /></ForceLightMode>} />
            <Route path="/launch/muncheye" element={<ForceLightMode><MunchEyeBanner /></ForceLightMode>} />
            <Route path="/founders" element={<ForceLightMode><Founders /></ForceLightMode>} />
            <Route path="/roadmap/public" element={<ForceLightMode><PublicRoadmap /></ForceLightMode>} />
            <Route path="/blog" element={<ForceLightMode><Blog /></ForceLightMode>} />
            <Route path="/blog/:slug" element={<ForceLightMode><BlogPost /></ForceLightMode>} />
            <Route path="/presentation" element={<ForceLightMode><Presentation /></ForceLightMode>} />
            {/* Client Portal Routes */}
            <Route path="/client/login" element={<ForceLightMode><ClientLogin /></ForceLightMode>} />
            <Route path="/client/dashboard" element={<ForceLightMode><ClientDashboard /></ForceLightMode>} />

            <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/quotes" element={<ProtectedRoute><Layout><Quotes /></Layout></ProtectedRoute>} />
            <Route path="/quotes/new" element={<ProtectedRoute><Layout><QuoteNew /></Layout></ProtectedRoute>} />
            <Route path="/quotes/:id/edit" element={<ProtectedRoute><Layout><QuoteEdit /></Layout></ProtectedRoute>} />
            <Route path="/quotes/:id" element={<ProtectedRoute><Layout><QuoteDetail /></Layout></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><Layout><Projects /></Layout></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><Layout><ProjectDetail /></Layout></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><Layout><Invoices /></Layout></ProtectedRoute>} />
            <Route path="/invoices/new/:clientId" element={<ProtectedRoute><Layout><InvoiceNew /></Layout></ProtectedRoute>} />
            <Route path="/invoices/:id/edit" element={<ProtectedRoute><Layout><InvoiceEdit /></Layout></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute><Layout><InvoiceDetail /></Layout></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><Layout><Clients /></Layout></ProtectedRoute>} />
            <Route path="/clients/new" element={<ProtectedRoute><Layout><ClientNew /></Layout></ProtectedRoute>} />
            <Route path="/clients/:id" element={<ProtectedRoute><Layout><ClientDetail /></Layout></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><Layout><Services /></Layout></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
            <Route path="/settings/booking" element={<ProtectedRoute><Layout><BookingSettings /></Layout></ProtectedRoute>} />
            <Route path="/settings/branding" element={<ProtectedRoute><Layout><BrandingSettings /></Layout></ProtectedRoute>} />
            <Route path="/settings/reminders" element={<ProtectedRoute><Layout><ReminderSettings /></Layout></ProtectedRoute>} />
            <Route path="/settings/sms" element={<ProtectedRoute><Layout><SMSNotificationSettings /></Layout></ProtectedRoute>} />
            <Route path="/settings/templates" element={<ProtectedRoute><Layout><Templates /></Layout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
            <Route path="/appointments" element={<ProtectedRoute><Layout><Appointments /></Layout></ProtectedRoute>} />
            <Route path="/appointments/:id" element={<ProtectedRoute><Layout><AppointmentDetail /></Layout></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
            <Route path="/support-tickets" element={<ProtectedRoute><Layout><Support /></Layout></ProtectedRoute>} />
            <Route path="/roadmap" element={<ProtectedRoute><Layout><Roadmap /></Layout></ProtectedRoute>} />
            <Route path="/settings/team" element={<ProtectedRoute><Layout><TeamMembers /></Layout></ProtectedRoute>} />
            <Route path="/settings/website" element={<ProtectedRoute><Layout><WebsiteSettings /></Layout></ProtectedRoute>} />
            <Route path="/settings/emails" element={<ProtectedRoute><Layout><EmailTemplates /></Layout></ProtectedRoute>} />
            <Route path="/settings/reviews" element={<ProtectedRoute><Layout><ReviewSettings /></Layout></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><Layout><CalendarView /></Layout></ProtectedRoute>} />
            <Route path="/tutorials" element={<ProtectedRoute><Layout><Tutorials /></Layout></ProtectedRoute>} />
            <Route path="/time-clock" element={<ProtectedRoute><Layout><TimeClock /></Layout></ProtectedRoute>} />
            <Route path="/time-clock/admin" element={<ProtectedRoute><Layout><TimeClockAdmin /></Layout></ProtectedRoute>} />
            <Route path="/clock/:orgId" element={<ForceLightMode><ClockKiosk /></ForceLightMode>} />

            {/* Admin Panel — separate layout, admin-only */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/organizations" element={<ProtectedRoute><AdminLayout><AdminOrganizations /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/support" element={<ProtectedRoute><AdminLayout><AdminSupport /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/inbox" element={<ProtectedRoute><AdminLayout><AdminInbox /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/broadcasts" element={<ProtectedRoute><AdminLayout><AdminBroadcasts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/videos" element={<ProtectedRoute><AdminLayout><AdminVideoAnalytics /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminLayout><AdminBlog /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ipn-simulator" element={<ProtectedRoute><AdminLayout><AdminIPNSimulator /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/scripts" element={<ProtectedRoute><AdminLayout><AdminScripts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/affiliates" element={<ProtectedRoute><AdminLayout><AdminAffiliates /></AdminLayout></ProtectedRoute>} />

            {/* Affiliate Program (public + portal) */}
            <Route path="/affiliates" element={<ForceLightMode><AffiliateLanding /></ForceLightMode>} />
            <Route path="/affiliate/dashboard" element={<ProtectedRoute><ForceLightMode><AffiliateDashboard /></ForceLightMode></ProtectedRoute>} />

            {/* Future features - backend ready, frontend hidden */}
            <Route path="/settings/domains" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            <Route path="/settings/calendar" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            <Route path="/settings/webhooks" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            <Route path="/settings/push" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />

            <Route path="/site/:handle" element={<ForceLightMode><SitePublic /></ForceLightMode>} />
            <Route path="/b/:handle" element={<ForceLightMode><BookingPublic /></ForceLightMode>} />
            <Route path="/embed/book/:handle" element={<ForceLightMode><BookingEmbed /></ForceLightMode>} />
            <Route path="/terms" element={<ForceLightMode><TermsOfService /></ForceLightMode>} />
            <Route path="/privacy" element={<ForceLightMode><PrivacyPolicy /></ForceLightMode>} />
            <Route path="/sms-consent" element={<ForceLightMode><SMSConsent /></ForceLightMode>} />
            <Route path="/support" element={<ForceLightMode><SupportDocs /></ForceLightMode>} />
            <Route path="/images" element={<ForceLightMode><Images /></ForceLightMode>} />
            <Route path="/unsubscribe" element={<ForceLightMode><Unsubscribe /></ForceLightMode>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;

